import React from "react";
import { Key, Pin, X } from "lucide-react";
import { SaddleCloth } from "./SaddleCloth";
import {
  computeDDPO,
  computeSLPO,
  NO_RATIO,
  leftMetrics,
  rightMetrics,
  fmtIq,
} from "../lib/excelHelpers";

const HeaderCell = ({ children, testId, colSpan }) => (
  <th
    data-testid={testId}
    colSpan={colSpan}
    className="px-3 py-3 text-[13px] tracking-[0.18em] uppercase font-semibold text-stone-800 border-b-2 border-stone-900 bg-[#e8dcc4]"
    style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
  >
    {children}
  </th>
);

const ReadCell = ({ children, testId, isErrValue = false, minWidth = 70 }) => {
  const isErr =
    isErrValue &&
    typeof children === "string" &&
    children.startsWith("#");
  const isNone = isErrValue && children === NO_RATIO;
  return (
    <td
      data-testid={testId}
      className="px-3 py-2 text-center border-b border-stone-300 bg-white"
      style={{
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        fontSize: "15px",
        color: isErr ? "#b91c1c" : isNone ? "#a8a29e" : "#0a0a0a",
        fontWeight: isErr ? 700 : 600,
        minWidth: `${minWidth}px`,
      }}
    >
      {children === "" || children === null || children === undefined ? (
        <span className="text-stone-300">·</span>
      ) : (
        children
      )}
    </td>
  );
};

// Editable columns on the LEFT half: DD (col 0), ML (col 1)
// Editable columns on the RIGHT half: SLLWP (col 2), SLML (col 3)
// Keyboard nav walks the whole editable set left→right so users can Tab through.
const EDITABLE_COLS_COUNT = 4;

const handleArrowNav = (e, rowIdx, colIdx, totalRows) => {
  const el = e.currentTarget;
  const atStart = el.selectionStart === 0 && el.selectionEnd === 0;
  const atEnd =
    el.selectionStart === el.value.length &&
    el.selectionEnd === el.value.length;

  let nextRow = rowIdx;
  let nextCol = colIdx;

  if (e.key === "ArrowUp") {
    if (rowIdx === 0) return;
    nextRow = rowIdx - 1;
  } else if (e.key === "ArrowDown" || e.key === "Enter") {
    if (rowIdx === totalRows - 1) return;
    nextRow = rowIdx + 1;
  } else if (e.key === "ArrowLeft") {
    if (!atStart) return;
    if (colIdx === 0) return;
    nextCol = colIdx - 1;
  } else if (e.key === "ArrowRight") {
    if (!atEnd) return;
    if (colIdx === EDITABLE_COLS_COUNT - 1) return;
    nextCol = colIdx + 1;
  } else if (e.key === "Tab") {
    return;
  } else {
    return;
  }

  e.preventDefault();
  const target = document.querySelector(
    `[data-cell-row="${nextRow}"][data-cell-col="${nextCol}"]`
  );
  if (target) {
    target.focus();
    const len = target.value.length;
    target.setSelectionRange(len, len);
  }
};

const ddBadge = (row) => {
  if (row.DDFlag === "unverified")
    return {
      text: "unverif.",
      cls: "text-amber-700",
      title: "No saved pre-race DD probables — this is Cybertote's will-pay page value, which is often wrong",
    };
  if (row.DDFlag === "mismatch")
    return {
      text: "CT≠",
      cls: "text-red-700",
      title: `From DD probables. Cybertote's will-pay page says $${row.DDCt}`,
    };
  return null;
};

const EditCell = ({
  value,
  onChange,
  testId,
  rowIdx,
  colIdx,
  totalRows,
  minWidth = 64,
  badge = null,
  badgeTestId,
}) => (
  <td
    className="relative px-2 py-1 border-b border-stone-300 bg-white"
    style={{ minWidth: `${minWidth}px` }}
  >
    {badge && (
      <span
        data-testid={badgeTestId}
        title={badge.title}
        className={`absolute top-0.5 right-1 text-[9px] font-bold uppercase tracking-wider cursor-help ${badge.cls}`}
        style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
      >
        {badge.text}
      </span>
    )}
    <input
      data-testid={testId}
      data-cell-row={rowIdx}
      data-cell-col={colIdx}
      type="text"
      inputMode="decimal"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => handleArrowNav(e, rowIdx, colIdx, totalRows)}
      className="w-full text-center bg-transparent outline-none border border-transparent rounded-sm px-2 py-1.5 focus:border-stone-900 focus:bg-[#fff8e6] hover:bg-stone-50 transition-colors"
      style={{
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        fontSize: "15px",
        color: "#0a0a0a",
        fontWeight: 500,
      }}
    />
  </td>
);

const SaddleTd = ({
  n,
  testId,
  mode,
  active = false,
  onToggle,
  isKey = false,
  onToggleKey,
  dim = false,
}) => {
  // mode: "elim" (left side) | "lock" (right side) | undefined (read-only)
  const post = Number(n);
  const canToggle = Number.isFinite(post) && typeof onToggle === "function";
  const canKey =
    mode === "elim" && Number.isFinite(post) && typeof onToggleKey === "function";
  const isElim = mode === "elim" && active;
  const isLock = mode === "lock" && active;
  const legLabel = mode === "elim" ? "leg1" : "leg2";
  const testIdToggle = canToggle ? `${mode}-${legLabel}-${post}` : undefined;
  const title = canToggle
    ? mode === "elim"
      ? active
        ? `#${post} eliminated — click to restore`
        : `Click to eliminate #${post} from consideration`
      : active
        ? `Leg 2 · #${post} locked in — click to unlock`
        : `Click to lock #${post} into Leg 2`
    : undefined;
  return (
    <td
      data-testid={testId}
      data-active={active ? "1" : "0"}
      className={`px-3 py-2 border-b border-stone-300 bg-[#0a0a0a] ${dim ? "opacity-40 grayscale" : ""}`}
      style={{ width: "60px" }}
    >
      <div className="relative flex justify-center">
        {canKey && (
          <button
            type="button"
            onClick={(e) => {
              // Guard against the parent saddle click also firing — a
              // single click on the key dot must NOT also toggle the
              // eliminate state behind it.
              e.stopPropagation();
              onToggleKey(post);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            data-testid={`key-leg1-${post}`}
            className={`absolute -top-1 -left-1 inline-flex items-center justify-center rounded-full transition ${
              isKey
                ? "bg-[#84cc16] text-[#0a0a0a] shadow-[0_0_0_2px_#0a0a0a,0_0_10px_#84cc1688]"
                : "bg-[#0a0a0a] text-[#78716c] border border-stone-700 hover:text-[#a3e635] hover:border-[#84cc16]"
            }`}
            style={{ width: 16, height: 16, zIndex: 2 }}
            title={
              isKey
                ? `Key horse #${post} — click to release`
                : `Click to lock #${post} as the Key horse`
            }
          >
            <Key size={10} strokeWidth={3} />
          </button>
        )}
        <button
          type="button"
          data-testid={testIdToggle}
          onClick={() => canToggle && onToggle(post)}
          disabled={!canToggle}
          title={title}
          className={`relative transition-transform ${
            canToggle ? "cursor-pointer hover:scale-105" : "cursor-default"
          } ${isLock ? "ring-2 ring-[#facc15] rounded-md" : ""} ${
            isKey ? "ring-2 ring-[#84cc16] rounded-md" : ""
          } ${isElim ? "opacity-50 grayscale" : ""}`}
          style={{ background: "transparent", border: 0, padding: 0 }}
        >
          <SaddleCloth n={n} />
          {isElim && (
            <>
              <span
                className="absolute inset-0 flex items-center justify-center pointer-events-none"
                aria-hidden="true"
              >
                <X
                  size={28}
                  strokeWidth={4}
                  color="#ef4444"
                  style={{ filter: "drop-shadow(0 0 2px #000)" }}
                />
              </span>
              <span
                className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center rounded-full"
                style={{
                  background: "#ef4444",
                  color: "#fff",
                  width: 16,
                  height: 16,
                  boxShadow: "0 0 0 2px #0a0a0a",
                }}
                aria-hidden="true"
              >
                <X size={10} strokeWidth={3} />
              </span>
            </>
          )}
          {isLock && (
            <span
              className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center rounded-full"
              style={{
                background: "#facc15",
                color: "#0a0a0a",
                width: 16,
                height: 16,
                boxShadow: "0 0 0 2px #0a0a0a",
              }}
              aria-hidden="true"
            >
              <Pin size={9} strokeWidth={3} />
            </span>
          )}
        </button>
      </div>
    </td>
  );
};

// Live win odds; Morning Line in grey with an "ML" tag until the Win pool reports.
const LiveOddsCell = ({ testId, row }) => {
  const live = String(row.LIVE ?? "").trim();
  const scratched = /^SC/i.test(live);
  const hasLive = !!live && !scratched && live !== "--";
  const text = scratched ? "SCR" : hasLive ? live : row.ML || "—";
  return (
    <td
      data-testid={testId}
      data-source={hasLive ? "live" : "ml"}
      className="px-3 py-2 text-center border-b border-stone-300 bg-white"
      style={{
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        fontSize: "15px",
        minWidth: 84,
        color: scratched ? "#b91c1c" : hasLive ? "#0a0a0a" : "#a8a29e",
        fontWeight: hasLive ? 700 : 400,
      }}
      title={hasLive ? "Live win odds" : scratched ? "Scratched" : "No live odds yet — Morning Line used"}
    >
      {text}
      {!hasLive && !scratched && row.ML && (
        <span className="ml-1 text-[9px] font-bold uppercase tracking-wider text-stone-400 align-top">
          ML
        </span>
      )}
    </td>
  );
};

// IQ Power cell; Cybertote $1,000 placeholders show "No Rank".
// ▲ moved up / ▼ dropped since the last refresh; fades in on change.
const MoveArrow = ({ delta, testId }) => {
  if (!delta) return null;
  const up = delta > 0;
  return (
    <span
      key={`${delta}`}
      data-testid={testId}
      data-delta={delta}
      title={`${up ? "Up" : "Down"} ${Math.abs(delta)} since the last refresh`}
      className={`ml-1 inline-flex items-center text-[10px] font-bold align-middle animate-in fade-in zoom-in duration-500 ${
        up ? "text-[#15803d]" : "text-[#b91c1c]"
      }`}
      style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
    >
      {up ? "▲" : "▼"}
      {Math.abs(delta)}
    </span>
  );
};

const IqCell = ({ testId, m, move = null }) => (
  <td
    data-testid={testId}
    className={`px-3 py-2 text-center border-b border-stone-300 ${m.noRank ? "bg-stone-200" : "bg-[#ecfccb]"}`}
    style={{
      fontFamily: "'JetBrains Mono', ui-monospace, monospace",
      fontSize: m.noRank ? "11px" : "15px",
      fontWeight: 700,
      minWidth: 88,
      color: m.noRank ? "#78716c" : m.iq == null ? "#a8a29e" : "#1a2e05",
      letterSpacing: m.noRank ? "0.12em" : undefined,
      textTransform: m.noRank ? "uppercase" : undefined,
    }}
    title={m.noRank ? "Cybertote $1,000 placeholder — not a real price" : undefined}
  >
    {m.noRank ? "No Rank" : fmtIq(m.iq)}
    {move}
  </td>
);

// Owner-only bonus points box; saves on Enter / blur.
const BonusCell = ({ testId, value, post, onSave }) => {
  const [v, setV] = React.useState(value ? String(value) : "");
  React.useEffect(() => setV(value ? String(value) : ""), [value]);
  const commit = () => {
    const n = v.trim() === "" ? 0 : Number(v);
    if (!Number.isFinite(n) || !Number.isFinite(Number(post)) || n === (Number(value) || 0)) return;
    onSave?.(Number(post), n);
  };
  return (
    <td className="px-1 py-1 text-center border-b border-stone-300 bg-[#fef9c3]">
      <input
        data-testid={testId}
        value={v}
        disabled={!Number.isFinite(Number(post)) || post === ""}
        onChange={(e) => setV(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        placeholder="0"
        inputMode="decimal"
        className="w-14 h-7 px-1 text-center rounded border border-stone-300 bg-white text-[13px] font-bold text-[#1a2e05] focus:outline-none focus:border-[#65a30d]"
        style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
      />
    </td>
  );
};

// Narrow solid-black column that visually separates the two halves.
const Divider = () => (
  <td
    className="border-b border-stone-900 bg-[#0a0a0a]"
    style={{ width: "10px", padding: 0 }}
    aria-hidden="true"
  />
);

const DividerHeader = () => (
  <th
    className="border-b-2 border-stone-900 bg-[#0a0a0a]"
    style={{ width: "10px", padding: 0 }}
    aria-hidden="true"
  />
);

export const RaceTable = ({
  leftRows,
  rightRows,
  onLeftChange,
  onRightChange,
  leftElims,
  leg2Locks,
  onToggleLeftElim,
  onToggleLeg2Lock,
  keyPost,
  onToggleKey,
  showHidden = false,
  rankMoves = { left: {}, right: {} },
  onBonus,
}) => {
  const totalRows = Math.max(leftRows.length, rightRows.length);
  const lm = leftMetrics(leftRows);
  const rm = rightMetrics(rightRows);
  const H = showHidden;
  const elimSet = leftElims instanceof Set ? leftElims : null;
  const lockSet = leg2Locks instanceof Set ? leg2Locks : null;

  return (
    <div
      data-testid="race-table-wrap"
      className="rounded-lg overflow-x-auto ring-1 ring-stone-900/15 shadow-[0_8px_30px_rgba(0,0,0,0.08)]"
      style={{ background: "#f5ecd6" }}
    >
      <table className="w-full border-collapse" data-testid="race-table">
        <thead>
          <tr>
            <HeaderCell testId="th-rank">Rank</HeaderCell>
            <HeaderCell testId="th-s">S</HeaderCell>
            {H && <HeaderCell testId="th-dd">DD</HeaderCell>}
            <HeaderCell testId="th-ml">ML</HeaderCell>
            <HeaderCell testId="th-live">Live Odds</HeaderCell>
            {H && <HeaderCell testId="th-ddpo">DDPO</HeaderCell>}
            {H && <HeaderCell testId="th-odds-rank">Odds Rk</HeaderCell>}
            {H && <HeaderCell testId="th-bonus">Bonus Pts</HeaderCell>}
            <HeaderCell testId="th-iq">IQ Power</HeaderCell>
            <DividerHeader />
            <HeaderCell testId="th-s2">S2</HeaderCell>
            {H && <HeaderCell testId="th-sllwp">SLLWP</HeaderCell>}
            <HeaderCell testId="th-slml">SLML</HeaderCell>
            {H && <HeaderCell testId="th-slpo">SLPO</HeaderCell>}
            {H && <HeaderCell testId="th-ml-rank">ML Rk</HeaderCell>}
            {H && <HeaderCell testId="th-bonus2">Bonus Pts</HeaderCell>}
            <HeaderCell testId="th-iq2">IQ Power</HeaderCell>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: totalRows }, (_, i) => {
            const L = leftRows[i] || { RANK: "", S: "", DD: "", ML: "" };
            const R = rightRows[i] || { S2: "", SLLWP: "", SLML: "" };
            const ddpo = computeDDPO(L.DD, L.ML);
            const slpo = computeSLPO(R.SLLWP, R.SLML);
            const lM = lm[i] || {};
            const rM = rm[i] || {};
            return (
              <tr key={`r-${i}`} data-testid={`row-${i}`}>
                <ReadCell testId={`cell-rank-${i}`} minWidth={60}>
                  {L.RANK}
                  <MoveArrow testId={`move-left-${i}`} delta={rankMoves.left[Number(L.S)]} />
                </ReadCell>
                <SaddleTd
                  n={L.S}
                  testId={`cell-s-${i}`}
                  mode="elim"
                  active={!!(elimSet && elimSet.has(Number(L.S)))}
                  onToggle={onToggleLeftElim}
                  isKey={Number.isFinite(Number(L.S)) && Number(L.S) === keyPost}
                  onToggleKey={onToggleKey}
                  dim={!!lM.noRank}
                />
                {H && <EditCell
                  testId={`input-dd-${i}`}
                  rowIdx={i}
                  colIdx={0}
                  totalRows={totalRows}
                  value={L.DD}
                  badge={ddBadge(L)}
                  badgeTestId={`dd-flag-badge-${i}`}
                  onChange={(v) => onLeftChange(i, "DD", v)}
                />}
                <EditCell
                  testId={`input-ml-${i}`}
                  rowIdx={i}
                  colIdx={1}
                  totalRows={totalRows}
                  value={L.ML}
                  onChange={(v) => onLeftChange(i, "ML", v)}
                />
                <LiveOddsCell testId={`cell-live-${i}`} row={L} />
                {H && (
                  <ReadCell testId={`cell-ddpo-${i}`} isErrValue minWidth={80}>
                    {ddpo}
                  </ReadCell>
                )}
                {H && (
                  <ReadCell testId={`cell-odds-rank-${i}`} isErrValue minWidth={64}>
                    {lM.oddsRank ?? NO_RATIO}
                  </ReadCell>
                )}
                {H && (
                  <BonusCell
                    testId={`input-bonus-${i}`}
                    value={L.BONUS}
                    post={L.S}
                    onSave={(post, pts) => onBonus?.("left", post, pts)}
                  />
                )}
                <IqCell testId={`cell-iq-${i}`} m={lM} />
                <Divider />
                <SaddleTd
                  n={R.S2}
                  testId={`cell-s2-${i}`}
                  mode="lock"
                  active={!!(lockSet && lockSet.has(Number(R.S2)))}
                  onToggle={onToggleLeg2Lock}
                  dim={!!rM.noRank}
                />
                {H && <EditCell
                  testId={`input-sllwp-${i}`}
                  rowIdx={i}
                  colIdx={2}
                  totalRows={totalRows}
                  value={R.SLLWP}
                  onChange={(v) => onRightChange(i, "SLLWP", v)}
                />}
                <EditCell
                  testId={`input-slml-${i}`}
                  rowIdx={i}
                  colIdx={3}
                  totalRows={totalRows}
                  value={R.SLML}
                  onChange={(v) => onRightChange(i, "SLML", v)}
                />
                {H && (
                  <ReadCell testId={`cell-slpo-${i}`} isErrValue minWidth={78}>
                    {slpo}
                  </ReadCell>
                )}
                {H && (
                  <ReadCell testId={`cell-ml-rank-${i}`} isErrValue minWidth={64}>
                    {rM.oddsRank ?? NO_RATIO}
                  </ReadCell>
                )}
                {H && (
                  <BonusCell
                    testId={`input-bonus2-${i}`}
                    value={R.BONUS}
                    post={R.S2}
                    onSave={(post, pts) => onBonus?.("right", post, pts)}
                  />
                )}
                <IqCell
                  testId={`cell-iq2-${i}`}
                  m={rM}
                  move={<MoveArrow testId={`move-right-${i}`} delta={rankMoves.right[Number(R.S2)]} />}
                />
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
